import { API_BASE_URL } from '../config.js';

/**
 * 056 FR-010/FR-014: read the reason the platform actually stated.
 *
 * The backend states it under `error` — its global error handler uses that key in all five
 * branches, and `modules/` sends `{ error }` 346 times against 48 uses of `{ message }`. This
 * client read only `message`, so a renter whose booking was refused because the car was already
 * taken for those dates saw nothing but `Request failed`. `message` is still honoured for the
 * minority of routes that use it.
 *
 * Only the top-level reason is read; the per-field `details` array is out of scope (contract C6).
 */
function refusalReason(payload: { error?: unknown; message?: unknown }, fallback: string): string {
  const stated = payload.error ?? payload.message;
  // `||` rather than `??`: an empty string is not a reason a person can act on.
  return (typeof stated === 'string' && stated.trim() ? stated : '') || fallback;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    // 213 (#309): the backend's `details` payload, spread into the response body — read here
    // rather than discarded. Only `alternatives` is populated today; absent on every refusal
    // that carries none.
    public readonly alternatives: unknown[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function getToken(): string | null {
  // 036: while impersonating, the app carries a distinct impersonation token so
  // any real customer session in this browser is preserved and restored on exit.
  return localStorage.getItem('impersonation_token') ?? localStorage.getItem('access_token');
}

/**
 * 299 — neither frontend ever called the existing POST /auth/refresh endpoint, so an expired
 * access token was always treated as a real logout even with a valid, unused refresh token
 * sitting in storage (issue #402, "keeps showing unauthorized"). Everything below makes the
 * *session* outlive one access token's lifetime; the endpoint itself is unchanged
 * (specs/299-token-refresh-flow/contracts/auth-refresh.md).
 *
 * Deliberately excluded (specs/299-token-refresh-flow/spec.md Assumptions):
 * - `/auth/login`, `/auth/register`, `/auth/refresh` itself — no prior session to renew, and
 *   including the refresh call would risk a loop.
 * - Impersonation sessions — a distinct, separately-scoped credential with no refresh token of
 *   its own; an expired impersonation token behaves exactly as it did before this feature.
 * - A request that carried no Authorization header at all — nothing to renew.
 */
const AUTH_EXEMPT_PATHS = new Set(['/auth/login', '/auth/register', '/auth/refresh']);

interface RefreshOutcome {
  accessToken: string | null;
  /** True only when the refresh call was explicitly rejected as invalid/expired — the one case
   * that ends the session. False for a network/server error, which must not look like a logout. */
  sessionEnded: boolean;
}

async function performRefresh(): Promise<RefreshOutcome> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return { accessToken: null, sessionEnded: true };

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    return { accessToken: null, sessionEnded: false };
  }

  // contracts/auth-refresh.md: only 401/403 are the endpoint explicitly saying the session is
  // over. Anything else not-ok (5xx, etc.) is treated the same as a network failure.
  if (res.status === 401 || res.status === 403) return { accessToken: null, sessionEnded: true };
  if (!res.ok) return { accessToken: null, sessionEnded: false };

  // contracts/auth-refresh.md: the pair comes back at the TOP LEVEL, unlike login/register's
  // { user, tokens: {...} } shape.
  const data = (await res.json().catch(() => null)) as {
    access_token?: string;
    refresh_token?: string;
  } | null;
  if (!data?.access_token || !data.refresh_token) return { accessToken: null, sessionEnded: false };

  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);
  return { accessToken: data.access_token, sessionEnded: false };
}

// Single-flight: several requests failing together on the same expired token must share one
// renewal rather than each racing to spend the backend's single-use refresh token against the
// others (specs/299-token-refresh-flow/research.md — User Story 2).
let refreshPromise: Promise<RefreshOutcome> | null = null;

function refreshSession(): Promise<RefreshOutcome> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function fetchWithAuth(path: string, init: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE_URL}${path}`, init);
  if (res.status !== 401) return res;
  if (AUTH_EXEMPT_PATHS.has(path)) return res;

  const hadAuthHeader = Boolean(
    (init.headers as Record<string, string> | undefined)?.Authorization,
  );
  if (!hadAuthHeader) return res;
  if (localStorage.getItem('impersonation_token')) return res;
  if (!localStorage.getItem('refresh_token')) return res;

  const outcome = await refreshSession();
  if (outcome.accessToken) {
    const headers = {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${outcome.accessToken}`,
    };
    return fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  }
  if (outcome.sessionEnded) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }
  // Either the session truly ended, or the renewal failed for an unrelated reason — either way
  // the original 401 is the correct thing to hand back to the caller's existing error handling.
  return res;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = getToken();
  // Content-Type only when a body is actually sent — Fastify 400s a JSON
  // Content-Type with an empty body (FST_ERR_CTP_EMPTY_JSON_BODY), which
  // silently broke body-less POSTs like /auth/impersonate/stop.
  const headers: Record<string, string> = {
    ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...opts.headers,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetchWithAuth(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(
      res.status,
      payload.code ?? 'UNKNOWN',
      refusalReason(payload, 'Request failed'),
      Array.isArray(payload.alternatives) ? payload.alternatives : [],
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * 059: fetch a privately-served file and hand back an object URL for it.
   *
   * Identity documents have no public or pre-signed URL by design (017 D5) — the platform
   * re-checks authorization on every fetch — so the only way to show one is to carry the
   * session's own credential. That credential is `getToken()`, not `access_token` directly:
   * during impersonation the two differ, and reading the wrong one fetches as the wrong
   * person. The caller owns the URL and must revoke it.
   *
   * The served type comes back with it: an object URL says nothing about what it points at,
   * and a preview has to know whether it is showing a photograph or a PDF.
   */
  getBlobUrl: async (path: string): Promise<{ url: string; type: string }> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetchWithAuth(path, { headers });

    if (!res.ok) {
      const payload = await res.json().catch(() => ({ error: res.statusText }));
      throw new ApiError(
        res.status,
        payload.code ?? 'UNKNOWN',
        refusalReason(payload, 'Could not load document'),
      );
    }

    const blob = await res.blob();
    return { url: URL.createObjectURL(blob), type: blob.type };
  },

  upload: async (path: string, formData: FormData): Promise<unknown> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetchWithAuth(path, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const payload = await res.json().catch(() => ({ error: res.statusText }));
      throw new ApiError(
        res.status,
        payload.code ?? 'UNKNOWN',
        refusalReason(payload, 'Upload failed'),
      );
    }

    return res.json();
  },
};
